/**
 * Sorts an object alphabetically by keys and sorts each nested array alphabetically
 * @param {Object} obj - Object with string keys and array values
 * @returns {Object} - New object with sorted keys and sorted arrays
 */
export const sortObjectWithArrays = (obj) => {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  // Get all keys and sort them alphabetically (case-insensitive)
  const sortedKeys = Object.keys(obj).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );

  // Create new object with sorted keys and sorted arrays
  const sortedObj = {};

  sortedKeys.forEach((key) => {
    const value = obj[key];

    // If the value is an array, sort it alphabetically (case-insensitive)
    if (Array.isArray(value)) {
      sortedObj[key] = [...value].sort((a, b) =>
        String(a).toLowerCase().localeCompare(String(b).toLowerCase())
      );
    } else {
      // If not an array, keep the original value
      sortedObj[key] = value;
    }
  });

  return sortedObj;
};

/**
 * Alternative version that sorts case-sensitive (uppercase letters come before lowercase)
 */
export const sortObjectWithArraysCaseSensitive = (obj) => {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  const sortedKeys = Object.keys(obj).sort();
  const sortedObj = {};

  sortedKeys.forEach((key) => {
    const value = obj[key];

    if (Array.isArray(value)) {
      sortedObj[key] = [...value].sort();
    } else {
      sortedObj[key] = value;
    }
  });

  return sortedObj;
};

/**
 * In-place sorting version (modifies original object)
 */
export const sortObjectWithArraysInPlace = (obj) => {
  if (!obj || typeof obj !== "object") {
    return obj;
  }

  // Sort each array in place
  Object.keys(obj).forEach((key) => {
    if (Array.isArray(obj[key])) {
      obj[key].sort((a, b) =>
        String(a).toLowerCase().localeCompare(String(b).toLowerCase())
      );
    }
  });

  // Create new object with sorted keys
  const sortedKeys = Object.keys(obj).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );

  const sortedObj = {};
  sortedKeys.forEach((key) => {
    sortedObj[key] = obj[key];
  });

  return sortedObj;
};

// Usage example:
// const fileDocGroups = {
//   Memos: ["Credit Memo"],
//   Appraisal: [
//     "Appraisal - Other",
//     "Commercial Narrative Appraisal PDF",
//     "Appraisal Amendment",
//     "Appraisal PDF",
//   ],
//   "Borrower Documents": [
//     "Background Report",
//     "Credit Report",
//     "Borrower Documents - Other",
//   ],
// };

// Sort the object
// const sortedFileDocGroups = sortObjectWithArrays(fileDocGroups);
// console.log(sortedFileDocGroups);

// Result will have:
// - Keys sorted alphabetically: "Appraisal", "Borrower Documents", "Memos"
// - Arrays sorted alphabetically within each key
